import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, IconButton, Paper,
    TextField, Typography, CircularProgress, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router-dom';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpenseCategoryService } from '../../services/expense-category.service';
import { ExpenseAdvanceService } from '../../services/expense-advance.service';
import { SegmentService } from '../../services/segment.service';
import {
    ExpenseAdvanceDTO,
    ExpenseCategoryDTO,
    ExpenseClaimLineRequest,
    ExpenseLineKind,
    ExpenseLimitViolation,
    CardTransactionDTO,
    RateLookups,
    VehicleType,
} from '../../interfaces/expense.interface';
import { ExpenseRulesService } from '../../services/expenseRules.service';
import { ExpenseCardService } from '../../services/expenseCard.service';
import { Segment } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import ExpenseReceiptControl from './ExpenseReceiptControl';
import DepartmentPicker, { useDepartmentOptions } from '../costcentre/DepartmentPicker'; // B4-H
import ProjectSelect, { useProjectOptions } from '../project/ProjectSelect'; // P1-A project
import { AuthService } from '../../services/auth.service'; // B4-H
import { formatAmount } from '../../utils/expenseDisplay';
import { toLocalIsoDate } from '../../utils/dateUtils';

interface LineRow extends ExpenseClaimLineRequest {
    key: string;
    amountText: string;
    // W3-U
    quantityText: string;
    quoteBasis?: string;
    quoteError?: string;
}

const KIND_OPTIONS: { id: ExpenseLineKind; name: string }[] = [
    { id: 'STANDARD', name: 'Standard' },
    { id: 'PER_DIEM', name: 'Per diem' },
    { id: 'MILEAGE', name: 'Mileage' },
    { id: 'CARD', name: 'Corporate card' },
];

const blankRow = (): LineRow => ({
    key: Math.random().toString(36).slice(2),
    expenseDate: toLocalIsoDate(),
    categoryId: '',
    description: '',
    amount: 0,
    amountText: '',
    receiptDocumentId: null,
    lineKind: 'STANDARD',
    quantityText: '',
});

const ExpenseClaimForm: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const toast = useToast();
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project

    const [title, setTitle] = useState('');
    const [periodFrom, setPeriodFrom] = useState(toLocalIsoDate());
    const [periodTo, setPeriodTo] = useState(toLocalIsoDate());
    const [lines, setLines] = useState<LineRow[]>([blankRow()]);
    const [categories, setCategories] = useState<ExpenseCategoryDTO[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [availableAdvances, setAvailableAdvances] = useState<ExpenseAdvanceDTO[]>([]);
    const [linkedAdvanceId, setLinkedAdvanceId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lookups, setLookups] = useState<RateLookups>({ locations: [], vehicleTypes: [], allowPerDiemOverride: false });
    const [cardTxns, setCardTxns] = useState<CardTransactionDTO[]>([]);
    const [warnings, setWarnings] = useState<ExpenseLimitViolation[]>([]);
    const [receiptNames, setReceiptNames] = useState<Record<string, string>>({}); // attachment id -> file name

    useEffect(() => {
        bootstrap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const bootstrap = async () => {
        setLoading(true);
        try {
            const [cats, mineAdvances, segs] = await Promise.all([
                ExpenseCategoryService.listByInstitution(0, 200),
                ExpenseAdvanceService.listMine(0, 100).catch(() => ({ content: [] as ExpenseAdvanceDTO[] })),
                SegmentService.getByInstitution(0, 200).catch(() => ({ content: [] as Segment[] })),
            ]);
            setCategories(cats.content.filter(c => c.isActive));
            ExpenseRulesService.lookups().then(setLookups).catch(() => undefined);
            ExpenseCardService.listMyTransactions()
                .then(all => setCardTxns(all.filter(t => t.status === 'UNMATCHED' || (id != null && t.claimId === id))))
                .catch(() => undefined);
            setSegments((segs.content as Segment[]).filter((s: Segment) => s.active !== false));
            setAvailableAdvances(mineAdvances.content.filter(a =>
                a.status === 'PAID' && (a.settlementClaimId == null || a.settlementClaimId === id)));
            if (isEdit && id) {
                const claim = await ExpenseClaimService.getById(id);
                if (claim.status !== 'DRAFT') {
                    toast.showError('Only DRAFT claims can be edited');
                    navigate(`/expense/my-claims/${id}`);
                    return;
                }
                setTitle(claim.title);
                setPeriodFrom(claim.periodFrom);
                setPeriodTo(claim.periodTo);
                setLinkedAdvanceId(claim.linkedAdvanceId ?? '');
                setLines(claim.lines.map(l => ({
                    key: l.id,
                    expenseDate: l.expenseDate,
                    categoryId: l.categoryId,
                    description: l.description ?? '',
                    amount: l.amount,
                    amountText: String(l.amount),
                    receiptDocumentId: l.receiptDocumentId ?? null,
                    segmentId: l.segmentId ?? undefined,
                    departmentId: l.departmentId ?? undefined, // B4-H
                    projectId: l.projectId ?? undefined, // P1-A project
                    lineKind: l.lineKind ?? 'STANDARD',
                    quantityText: l.quantity != null ? String(l.quantity) : '',
                    quantity: l.quantity ?? undefined,
                    locationId: l.locationId ?? undefined,
                    vehicleType: l.vehicleType ?? undefined,
                    routeText: l.routeText ?? undefined,
                    cardTransactionId: l.cardTransactionId ?? undefined,
                })));
                setWarnings(claim.limitWarnings ?? []);
                ExpenseClaimService.listReceipts(id)
                    .then(list => setReceiptNames(Object.fromEntries(list.map(r => [r.id, r.fileName]))))
                    .catch(() => undefined);
            }
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load claim data');
        } finally {
            setLoading(false);
        }
    };

    // Server-side pricing preview for per-diem / mileage lines (the server re-prices on save; this is only a preview).
    const quoteKey = lines.map(l => [l.key, l.lineKind, l.expenseDate, l.quantityText, l.locationId, l.vehicleType].join('|')).join(';');
    useEffect(() => {
        const timer = setTimeout(() => {
            lines.forEach(l => {
                const kind = l.lineKind ?? 'STANDARD';
                const qty = Number(l.quantityText);
                if ((kind !== 'PER_DIEM' && kind !== 'MILEAGE') || !(qty > 0)) return;
                if (kind === 'PER_DIEM' && !l.locationId) return;
                if (kind === 'MILEAGE' && !l.vehicleType) return;
                ExpenseRulesService.quote({
                    kind, date: l.expenseDate, quantity: qty,
                    locationId: l.locationId, vehicleType: l.vehicleType,
                }).then(q => {
                    setLines(prev => prev.map(x => {
                        if (x.key !== l.key) return x;
                        const keepManual = kind === 'PER_DIEM' && lookups.allowPerDiemOverride && x.amountText !== '' && x.quoteBasis === 'manual';
                        return { ...x, amountText: keepManual ? x.amountText : String(q.amount), quoteBasis: keepManual ? 'manual' : q.basis, quoteError: undefined };
                    }));
                }).catch((e: any) => {
                    const msg = e?.response?.data?.message ?? 'No rate found';
                    setLines(prev => prev.map(x => (x.key === l.key ? { ...x, amountText: '', quoteBasis: undefined, quoteError: msg } : x)));
                });
            });
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quoteKey, lookups.allowPerDiemOverride]);

    const chooseKind = (key: string, kind: ExpenseLineKind) => {
        updateLine(key, {
            lineKind: kind, amountText: '', quantityText: '', quantity: undefined, locationId: undefined,
            vehicleType: undefined, routeText: undefined, cardTransactionId: undefined,
            quoteBasis: undefined, quoteError: undefined,
        });
    };

    const chooseCardTxn = (key: string, txnId: string) => {
        const t = cardTxns.find(x => x.id === txnId);
        updateLine(key, {
            cardTransactionId: txnId || undefined,
            amountText: t ? String(t.amount) : '',
            ...(t ? { expenseDate: t.txnDate, description: t.description ?? undefined } : {}),
        });
    };

    const total = useMemo(
        () => lines.reduce((sum, l) => sum + (Number(l.amountText) || 0), 0),
        [lines],
    );

    const updateLine = (key: string, patch: Partial<LineRow>) => {
        setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));
    };

    const removeLine = (key: string) => {
        setLines(prev => (prev.length === 1 ? prev : prev.filter(l => l.key !== key)));
    };

    const addLine = () => setLines(prev => [...prev, blankRow()]);

    const validate = (): string | null => {
        if (!title.trim()) return 'Title is required';
        if (!periodFrom || !periodTo) return 'Period is required';
        if (periodFrom > periodTo) return 'Period start must be on or before period end';
        if (lines.length === 0) return 'Add at least one line';
        for (const l of lines) {
            if (!l.categoryId) return 'Each line must have a category';
            if (!l.expenseDate) return 'Each line must have a date';
            const kind = l.lineKind ?? 'STANDARD';
            if (kind === 'PER_DIEM' && (!l.locationId || !(Number(l.quantityText) > 0))) return 'A per-diem line needs a location and the number of days';
            if (kind === 'MILEAGE' && (!l.vehicleType || !(Number(l.quantityText) > 0))) return 'A mileage line needs a vehicle type and the distance in km';
            if (kind === 'MILEAGE' && !l.routeText?.trim()) return 'A mileage line needs a route';
            if (kind === 'CARD' && !l.cardTransactionId) return 'A card line needs a card transaction';
            const amt = Number(l.amountText);
            if (kind === 'STANDARD' && (!Number.isFinite(amt) || amt <= 0)) return 'Each line amount must be > 0';
            if (l.expenseDate < periodFrom || l.expenseDate > periodTo) {
                return `Line date ${l.expenseDate} is outside the period`;
            }
        }
        return null;
    };

    const handleSave = async () => {
        const error = validate();
        if (error) {
            toast.showError(error);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: title.trim(),
                periodFrom,
                periodTo,
                lines: lines.map(l => ({
                    expenseDate: l.expenseDate,
                    categoryId: l.categoryId,
                    description: l.description?.trim() || null,
                    // the server prices per-diem/mileage/card lines; only STANDARD (or an allowed per-diem override) sends an amount
                    ...(l.lineKind === 'STANDARD' || l.lineKind == null || (l.lineKind === 'PER_DIEM' && lookups.allowPerDiemOverride && l.quoteBasis === 'manual')
                        ? { amount: Number(l.amountText) } : {}),
                    receiptDocumentId: l.receiptDocumentId,
                    ...(l.segmentId ? { segmentId: l.segmentId } : {}),
                    ...(l.departmentId ? { departmentId: l.departmentId } : {}), // B4-H
                    ...(l.projectId ? { projectId: l.projectId } : {}), // P1-A project
                    lineKind: l.lineKind ?? 'STANDARD',
                    ...(l.quantityText ? { quantity: Number(l.quantityText) } : {}),
                    ...(l.locationId ? { locationId: l.locationId } : {}),
                    ...(l.vehicleType ? { vehicleType: l.vehicleType } : {}),
                    ...(l.routeText ? { routeText: l.routeText } : {}),
                    ...(l.cardTransactionId ? { cardTransactionId: l.cardTransactionId } : {}),
                })),
            };
            let saved = isEdit && id
                ? await ExpenseClaimService.updateDraft(id, payload)
                : await ExpenseClaimService.createDraft(payload);
            const desiredAdvanceId = linkedAdvanceId || '';
            const currentAdvanceId = saved.linkedAdvanceId ?? '';
            if (desiredAdvanceId !== currentAdvanceId) {
                if (desiredAdvanceId) {
                    saved = await ExpenseClaimService.linkAdvance(saved.id, desiredAdvanceId);
                } else if (currentAdvanceId) {
                    saved = await ExpenseClaimService.unlinkAdvance(saved.id);
                }
            }
            const breaches = saved.limitWarnings ?? [];
            if (breaches.length > 0) {
                toast.showError(`Saved, but ${breaches.length} line(s) exceed a limit: ${breaches[0].message}`);
            } else {
                toast.showSuccess(isEdit ? 'Claim updated' : 'Draft saved');
            }
            navigate(`/expense/my-claims/${saved.id}`);
        } catch (e: any) {
            toast.showApiError(e, 'Failed to save claim');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box data-help-page="expense.claimForm.page" sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {isEdit ? 'Edit Draft Claim' : 'New Expense Claim'}
            </Typography>
            <Paper data-help-section="expense.claimForm.section.header" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        data-help="expense.claimForm.field.title" label="Title" value={title} onChange={e => setTitle(e.target.value)}
                        sx={{ flex: '2 1 240px', minWidth: 0 }}
                    />
                    <TextField
                        data-help="expense.claimForm.field.periodFrom" label="Period from" type="date" value={periodFrom}
                        onChange={e => setPeriodFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 160px', maxWidth: 260, minWidth: 0 }}
                    />
                    <TextField
                        data-help="expense.claimForm.field.periodTo" label="Period to" type="date" value={periodTo}
                        onChange={e => setPeriodTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 160px', maxWidth: 260, minWidth: 0 }}
                    />
                </Box>
                {availableAdvances.length > 0 && (
                    <SearchableSelect<ExpenseAdvanceDTO>
                        options={availableAdvances}
                        value={linkedAdvanceId}
                        onChange={id => setLinkedAdvanceId(id)}
                        getOptionLabel={a => `${formatAmount(a.amount)} — ${a.reason}`}
                        data-help="expense.claimForm.field.advance" label="Link cash advance (optional)"
                        placeholder="None"
                        sx={{ mt: 2 }}
                    />
                )}
            </Paper>

            {warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="limit-warnings">
                    {warnings.map((w, i) => <div key={i}>{w.message}</div>)}
                </Alert>
            )}

            {/* Line rows are wrapping flex cells (not a fixed-column table) so they stack on a phone. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {lines.map((l, idx) => (
                    <Paper key={l.key} data-help-section="expense.claimForm.section.line" variant="outlined" sx={{ p: 1.5 }} data-testid="claim-line-row">
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                            <TextField
                                type="date" size="small" data-help="expense.claimForm.field.lineDate" label="Date" value={l.expenseDate}
                                InputLabelProps={{ shrink: true }}
                                onChange={e => updateLine(l.key, { expenseDate: e.target.value })}
                                sx={{ flex: '1 1 150px', maxWidth: 220, minWidth: 0 }}
                            />
                            <SearchableSelect<{ id: ExpenseLineKind; name: string }>
                                options={KIND_OPTIONS}
                                value={l.lineKind ?? 'STANDARD'}
                                onChange={id => chooseKind(l.key, (id || 'STANDARD') as ExpenseLineKind)}
                                getOptionLabel={k => k.name}
                                data-help="expense.claimForm.field.lineKind" label="Line type"
                                size="small"
                                clearable={false}
                                sx={{ flex: '1 1 150px', maxWidth: 200, minWidth: 0 }}
                            />
                            <SearchableSelect<ExpenseCategoryDTO>
                                options={categories}
                                value={l.categoryId}
                                onChange={id => updateLine(l.key, { categoryId: id })}
                                getOptionLabel={c => c.name}
                                data-help="expense.claimForm.field.category" label="Category"
                                size="small"
                                sx={{ flex: '1 1 180px', maxWidth: 300, minWidth: 0 }}
                            />
                            <TextField
                                size="small" data-help="expense.claimForm.field.description" label="Description" value={l.description ?? ''}
                                onChange={e => updateLine(l.key, { description: e.target.value })}
                                sx={{ flex: '2 1 220px', minWidth: 0 }}
                            />
                            <TextField
                                size="small" type="number" data-help="expense.claimForm.field.amount" label="Amount" inputProps={{ min: 0, step: '0.01' }}
                                value={l.amountText}
                                onChange={e => updateLine(l.key, { amountText: e.target.value, quoteBasis: 'manual' })}
                                disabled={(l.lineKind ?? 'STANDARD') !== 'STANDARD'
                                    && !((l.lineKind === 'PER_DIEM') && lookups.allowPerDiemOverride)}
                                helperText={(l.lineKind ?? 'STANDARD') === 'STANDARD' ? undefined : (l.quoteError ?? l.quoteBasis)}
                                error={Boolean(l.quoteError)}
                                sx={{ flex: '1 1 120px', maxWidth: 180, minWidth: 0 }}
                            />
                            {segments.length > 0 && (
                                <SearchableSelect<Segment>
                                    options={segments}
                                    value={l.segmentId ?? ''}
                                    onChange={id => updateLine(l.key, { segmentId: id || undefined })}
                                    getOptionLabel={s => `${s.code} — ${s.name}`}
                                    data-help="expense.claimForm.field.segment" label="Segment"
                                    placeholder="(default)"
                                    size="small"
                                    sx={{ flex: '1 1 180px', maxWidth: 300, minWidth: 0 }}
                                />
                            )}
                            {/* B4-H */}
                            {departmentOptions.length > 0 && <Box sx={{ flex: '1 1 180px', maxWidth: 300, minWidth: 0 }}><DepartmentPicker
                                options={departmentOptions}
                                value={l.departmentId ?? ''}
                                onChange={dep => updateLine(l.key, { departmentId: dep || undefined })}
                                data-help="expense.claimForm.field.department" label="Department"
                                placeholder="(my department)"
                                size="small"
                            /></Box>}
                            {/* end B4-H */}
                            {projectOptions.length > 0 && <Box sx={{ flex: '1 1 180px', maxWidth: 300, minWidth: 0 }}>{/* P1-A project */}<ProjectSelect
                                options={projectOptions}
                                value={l.projectId ?? ''}
                                onChange={pid => updateLine(l.key, { projectId: pid || undefined })}
                                data-help="expense.claimForm.field.project" label="Project"
                                size="small"
                                data-testid={`claim-project-${l.key}`}
                            /></Box>}
                            {l.lineKind === 'PER_DIEM' && (
                                <>
                                    <SearchableSelect<RateLookups['locations'][number]>
                                        options={lookups.locations}
                                        value={l.locationId ?? ''}
                                        onChange={id => updateLine(l.key, { locationId: id || undefined })}
                                        getOptionLabel={x => x.name}
                                        data-help="expense.claimForm.field.location" label="Location"
                                        size="small"
                                        sx={{ flex: '1 1 160px', maxWidth: 260, minWidth: 0 }}
                                    />
                                    <TextField
                                        size="small" type="number" data-help="expense.claimForm.field.days" label="Days" inputProps={{ min: 0.5, step: 0.5 }}
                                        value={l.quantityText}
                                        onChange={e => updateLine(l.key, { quantityText: e.target.value })}
                                        sx={{ flex: '1 1 100px', maxWidth: 140, minWidth: 0 }}
                                    />
                                </>
                            )}
                            {l.lineKind === 'MILEAGE' && (
                                <>
                                    <SearchableSelect<{ id: VehicleType; name: string }>
                                        options={lookups.vehicleTypes.map(v => ({ id: v, name: v }))}
                                        value={l.vehicleType ?? ''}
                                        onChange={id => updateLine(l.key, { vehicleType: (id || undefined) as VehicleType | undefined })}
                                        getOptionLabel={x => x.name}
                                        data-help="expense.claimForm.field.vehicle" label="Vehicle"
                                        size="small"
                                        sx={{ flex: '1 1 140px', maxWidth: 200, minWidth: 0 }}
                                    />
                                    <TextField
                                        size="small" type="number" data-help="expense.claimForm.field.distance" label="Distance (km)" inputProps={{ min: 0, step: '0.1' }}
                                        value={l.quantityText}
                                        onChange={e => updateLine(l.key, { quantityText: e.target.value })}
                                        sx={{ flex: '1 1 120px', maxWidth: 160, minWidth: 0 }}
                                    />
                                    <TextField
                                        size="small" data-help="expense.claimForm.field.route" label="Route" required inputProps={{ maxLength: 300 }} value={l.routeText ?? ''}
                                        onChange={e => updateLine(l.key, { routeText: e.target.value })}
                                        sx={{ flex: '1 1 180px', maxWidth: 300, minWidth: 0 }}
                                    />
                                </>
                            )}
                            {l.lineKind === 'CARD' && (
                                <SearchableSelect<CardTransactionDTO>
                                    options={cardTxns.filter(t => t.id === l.cardTransactionId
                                        || !lines.some(o => o.key !== l.key && o.cardTransactionId === t.id))}
                                    value={l.cardTransactionId ?? ''}
                                    onChange={id => chooseCardTxn(l.key, id)}
                                    getOptionLabel={t => `${t.txnDate} ${t.cardLabel ?? ''} ${formatAmount(t.amount)} ${t.description ?? ''}`.trim()}
                                    data-help="expense.claimForm.field.cardTransaction" label="Card transaction"
                                    size="small"
                                    noOptionsText="No unmatched card transactions"
                                    sx={{ flex: '2 1 280px', minWidth: 0 }}
                                />
                            )}
                            <ExpenseReceiptControl
                                claimId={isEdit ? id : undefined}
                                receiptId={l.receiptDocumentId}
                                fileName={l.receiptDocumentId ? receiptNames[l.receiptDocumentId] : undefined}
                                amount={Number(l.amountText)}
                                threshold={categories.find(c => c.id === l.categoryId)?.receiptRequiredAboveAmount ?? lookups.defaultReceiptThreshold}
                                onAttached={r => {
                                    setReceiptNames(prev => ({ ...prev, [r.id]: r.fileName }));
                                    updateLine(l.key, { receiptDocumentId: r.id });
                                }}
                                onRemoved={() => updateLine(l.key, { receiptDocumentId: null })}
                            />
                            <IconButton
                                size="small" onClick={() => removeLine(l.key)}
                                disabled={lines.length === 1}
                                data-help="expense.claimForm.removeLine" aria-label={`Remove line ${idx + 1}`}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Paper>
                ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button data-help="expense.claimForm.addLine" startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
                <Typography variant="h6">Total: {formatAmount(total)}</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button data-help="expense.claimForm.cancel" variant="outlined" onClick={() => navigate('/expense/my-claims')}>
                    Cancel
                </Button>
                <Button data-help="expense.claimForm.save" variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save draft'}
                </Button>
            </Box>
        </Box>
    );
};

export default ExpenseClaimForm;
